import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { EntityRow, RulesApiService } from 'src/app/core/services/rules-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  filterRecordsBySearch,
  filterRecordsByStatus,
  paginateClientRecords,
} from 'src/app/shared/utils/client-list.util';
import {
  catalogStatusBadgeClass,
  catalogStatusLabel,
} from 'src/app/shared/utils/catalog-status-badge.util';
import { numberFromDecimalLike } from 'src/app/shared/utils/money-field-form';

interface ConfigDraft {
  id?: number;
  task_code: string;
  model_id: number | null;
  max_tokens: number | null;
  temperature: number | null;
}

@Component({
  selector: 'app-llm-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
    IconModule,
  ],
  templateUrl: './llm-config.component.html',
})
export class LlmConfigComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search task code, model…' },
      status: {
        options: [
          { value: '', label: 'All status' },
          { value: 'DRAFT', label: 'Draft' },
          { value: 'PUBLISHED', label: 'Published' },
        ],
      },
      dateRange: false,
    },
    pagination: true,
  };

  readonly columns: ListModuleColumnDef[] = [
    { id: 'task_code', header: 'Task' },
    { id: 'version', header: 'Version' },
    { id: 'model', header: 'Model' },
    { id: 'temperature', header: 'Temperature' },
    { id: 'max_tokens', header: 'Max tokens' },
    { id: 'status', header: 'Status' },
    { id: 'actions', header: 'Actions', align: 'right' },
  ];

  records: EntityRow[] = [];
  models: EntityRow[] = [];
  draft: ConfigDraft | null = null;
  loading = false;
  saving = false;
  error = '';
  notice = '';

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  currentPage = 1;
  readonly perPage = 10;

  readonly statusBadgeClass = catalogStatusBadgeClass;
  readonly statusLabel = catalogStatusLabel;

  ngOnInit(): void {
    this.load();
  }

  get filteredRecords(): EntityRow[] {
    let rows = [...this.records];
    rows = filterRecordsBySearch(rows, this.listFilters.searchQuery, [
      'task_code',
      'status',
    ]);
    rows = filterRecordsByStatus(rows, this.listFilters.status, 'status');
    return rows;
  }

  get pagedRecords(): EntityRow[] {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).pageRows;
  }

  get paginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).meta;
  }

  seeding = false;

  get emptyMessage(): string {
    if (this.records.length) return 'No configs match your filters.';
    return 'No LLM task configs yet — use “Create default configs” to start from the shipped defaults.';
  }

  /**
   * Fills a fresh environment with the default task configs.
   *
   * Idempotent on the server: it skips any task code that already exists, so pressing it twice
   * cannot duplicate or overwrite what an operator has tuned.
   */
  seedDefaults(): void {
    if (this.seeding) return;
    this.seeding = true;
    this.error = '';
    this.notice = '';
    this.api
      .seedLlmConfigDefaults()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => {
          this.seeding = false;
          this.notice = `Created ${res.data?.created ?? 0} task config(s).`;
          this.load();
        },
        error: (err) => {
          this.seeding = false;
          this.error = resolveApiErrorMessage(err);
        },
      });
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .listLlmModels()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => (this.models = res.data ?? []),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
    this.api
      .listLlmConfigRecords()
      .pipe(assertSuccessEnvelope(), finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.records = res.data ?? [];
          this.currentPage = 1;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.currentPage = 1;
  }

  onListPageChange(page: number): void {
    this.currentPage = page;
  }

  edit(record: EntityRow): void {
    this.notice = '';
    this.draft = {
      id: Number(record['id']),
      task_code: String(record['task_code']),
      model_id: Number(record['model_id']),
      max_tokens: Number(record['max_tokens']),
      temperature: numberFromDecimalLike(record['temperature']),
    };
  }

  newVersion(record: EntityRow): void {
    this.edit(record);
    if (this.draft) this.draft.id = undefined;
  }

  cancel(): void {
    this.draft = null;
  }

  save(): void {
    if (!this.draft) return;
    const body = {
      task_code: this.draft.task_code,
      model_id: this.draft.model_id,
      max_tokens: this.draft.max_tokens,
      ...(this.draft.temperature == null ? {} : { temperature: this.draft.temperature }),
    };
    const request$ = this.draft.id
      ? this.api.updateLlmConfigRecord(this.draft.id, body)
      : this.api.createLlmConfigRecord(body);

    this.saving = true;
    this.error = '';
    request$
      .pipe(assertSuccessEnvelope(), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notice = 'Draft saved. Publish it to make it live.';
          this.draft = null;
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  publish(record: EntityRow): void {
    this.saving = true;
    this.error = '';
    this.api
      .publishLlmConfigRecord(Number(record['id']))
      .pipe(assertSuccessEnvelope(), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notice =
            'Published. ai-service will pick it up on its next config refresh (up to ~5 minutes).';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  modelName(record: EntityRow): string {
    const model = record['model'];
    if (model && typeof model === 'object') {
      const name = (model as Record<string, unknown>)['display_name'];
      if (typeof name === 'string') return name;
    }
    return '—';
  }

  formatTemperature(record: EntityRow): string {
    const value = numberFromDecimalLike(record['temperature']);
    return value == null ? '—' : String(value);
  }

  isDraft(record: EntityRow): boolean {
    return record['status'] === 'DRAFT';
  }
}
